import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { yupResolver } from "@hookform/resolvers/yup";
import { endOfMonth } from "date-fns";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { generatePath, NavLink } from "react-router-dom";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import CompanyLogo from "~/components/CompanyLogo";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormInputError from "~/components/formElements/FormInputError";
import Input from "~/components/formElements/Input";
import { SelectPlain } from "~/components/formElements/Select/Select.tsx";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import Headline from "~/components/Headline";
import Hint from "~/components/Hint";
import WordIcon from "~/components/icons/WordIcon";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { PROJECT_DETAILS_PATH_WITH_TAB_ID, QUOTA_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type { InvoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import {
	updatePendingRegularOrAdvanceInvoiceStatusToDraft,
	updatePendingSelfBilledInvoiceStatusToDraft,
} from "~/modules/billing/api/pendingInvoice/pendingInvoiceApiDispatchers.ts";
import type { PendingInvoiceStatusReportData } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import InvoiceDetailsElement from "~/modules/billing/components/InvoiceDetailsElement";
import InvoiceTotalsFormSection from "~/modules/billing/components/InvoiceTotalsFormSection";
import type { PendingInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import UsersMonthlyClosingStatus from "~/modules/billing/components/UsersMonthlyClosingStatus";
import { DEFAULT_PAYMENT_TARGET_DAYS, DEFAULT_VAT_PERCENTAGE } from "~/modules/billing/constants/billingConstants.ts";
import useInvoiceDetailsByDeliveryPeriod from "~/modules/billing/hooks/useInvoiceDetailsByDeliveryPeriod.ts";
import { useInvoiceDownload } from "~/modules/billing/hooks/useInvoiceDownload.ts";
import { useInvoiceTypeSelectOptions } from "~/modules/billing/hooks/useInvoiceTypeSelectOptions.ts";
import { ProjectDetailsTabName } from "~/modules/project/types/projectViewTypes.ts";
import type { User, UserWithConfidentialInformation } from "~/modules/user/api/user/userTypes.ts";
import { formatDateToYYYYMMDD, formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

type FormData = {
	comment: string | null;
	deliveryStartDate: Date;
	deliveryEndDate: Date;
	invoiceNumber: string;
	invoiceReceivedAt: Date;
	paymentTargetDays: number;
	senderInformation: string | null;
	subtotalCents: number;
	totalAmountCents: number;
	travelCostCentsBeforeVat: number;
	vatPercentage: number;
};

type Props = {
	allUsersData: User[] | UserWithConfidentialInformation[];
	initialInvoiceTypeId: InvoiceTypeId;
	onCancel: () => void;
	onSuccess: () => void;
	pendingInvoiceData: PendingInvoicesIndexTableData;
	userCanManageInvoices: boolean;
};

const endOfCurrentMonth = endOfMonth(new Date());

const StartBillingProcessForProjectInvoicesForm = ({
	allUsersData,
	initialInvoiceTypeId,
	onCancel,
	onSuccess,
	pendingInvoiceData,
	userCanManageInvoices,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [selectedInvoiceTypeId, setSelectedInvoiceTypeId] = useState<InvoiceTypeId>(initialInvoiceTypeId);
	const { isGenerating, handleDownload } = useInvoiceDownload(pendingInvoiceData.id);

	const isAdvanceInvoice = pendingInvoiceData.invoiceTypeId === invoiceTypeId.AdvanceInvoice;
	const isSelfBilled = selectedInvoiceTypeId === invoiceTypeId.SelfBilledInvoice;
	const hasMissingTimeTrackings = pendingInvoiceData.subtotalCents === 0;

	const invoiceTypeSelectOptions = useInvoiceTypeSelectOptions();

	// ── Derived display data ──────────────────────────────────────────
	const orderManagerFullName = useMemo(() => {
		if (!pendingInvoiceData.orderManagedBy) return null;
		return allUsersData.find((user) => user.id === pendingInvoiceData.orderManagedBy)?.fullName ?? null;
	}, [allUsersData, pendingInvoiceData.orderManagedBy]);

	const phaseTitles = useMemo(() => {
		const phasesSorted = [...pendingInvoiceData.phases].sort(byObjectProperty("phaseNumber", "desc"));
		const primaryPhaseData = phasesSorted[0];
		if (pendingInvoiceData.phases.length === 1) {
			return (
				<NavLink
					to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
						projectId: pendingInvoiceData.projectId!,
						tabId: "phase-" + primaryPhaseData.phaseNumber,
					})}
					className="hover:text-secondary-500"
				>
					{primaryPhaseData.phaseTitle}
				</NavLink>
			);
		}
		return "diverse";
	}, [pendingInvoiceData.phases, pendingInvoiceData.projectId]);

	const phaseSectionTitle = pendingInvoiceData.phases.length > 1 ? "Phasen" : "Phase";

	const orderTitle = useMemo(() => {
		if (pendingInvoiceData.orders.length === 1) {
			return pendingInvoiceData.orders[0].title;
		}
		return "diverse";
	}, [pendingInvoiceData.orders]);

	// ── Form setup ────────────────────────────────────────────────────

	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().nullable().default(""),
			deliveryStartDate: yup.date().required(),
			deliveryEndDate: yup
				.date()
				.required()
				.min(yup.ref("deliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen.")
				.default(null),
			invoiceNumber: yup.string().default("").when([], {
				is: () => isSelfBilled,
				then: (s) => s.required("Rechnungsnummer ist erforderlich."),
				otherwise: (s) => s.nullable().default(null),
			}),
			invoiceReceivedAt: yup.date().when([], {
				is: () => isSelfBilled,
				then: (s) => s.required(),
				otherwise: (s) => s.nullable().default(null),
			}).default(null),
			paymentTargetDays: yup.number().required().default(DEFAULT_PAYMENT_TARGET_DAYS),
			senderInformation: yup.string().nullable().default(""),
			subtotalCents: yup.number().required(),
			totalAmountCents: yup.number().required().default(0),
			travelCostCentsBeforeVat: yup.number().when([], {
				is: () => isAdvanceInvoice,
				then: (s) => s.default(0),
				otherwise: (s) => s.required(),
			}).default(0),
			vatPercentage: yup.number().required(),
		});
	}, [isSelfBilled, isAdvanceInvoice]);

	const defaultValues = useMemo(() => {
		let paymentTargetDays = pendingInvoiceData.paymentTargetDays || DEFAULT_PAYMENT_TARGET_DAYS;

		if(pendingInvoiceData.orders?.length === 1 && pendingInvoiceData.orders[0].paymentTargetDays) {
			paymentTargetDays = pendingInvoiceData.orders[0].paymentTargetDays;
		}

		return {
			comment: pendingInvoiceData.comment || "",
			deliveryStartDate: pendingInvoiceData.deliveryStartDate,
			deliveryEndDate: pendingInvoiceData.deliveryEndDate,
			invoiceNumber: "",
			invoiceReceivedAt: new Date(),
			paymentTargetDays,
			senderInformation: "",
			subtotalCents: pendingInvoiceData.subtotalCents,
			totalAmountCents: pendingInvoiceData.totalAmountCents,
			travelCostCentsBeforeVat: 0,
			vatPercentage: DEFAULT_VAT_PERCENTAGE,
		};
	}, [pendingInvoiceData.comment, pendingInvoiceData.deliveryEndDate, pendingInvoiceData.deliveryStartDate, pendingInvoiceData.orders, pendingInvoiceData.paymentTargetDays, pendingInvoiceData.subtotalCents, pendingInvoiceData.totalAmountCents]);

	const {
		handleSubmit,
		control,
		formState: { errors, isSubmitted, isValid },
		setValue,
		watch,
	} = useForm<FormData>({
		defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const deliveryStartDate = watch("deliveryStartDate");
	const deliveryEndDate = watch("deliveryEndDate");

	const handlePreCheckComplete = useCallback(
		(data: PendingInvoiceStatusReportData) => {
			setValue("subtotalCents", data.subtotalCents);
		},
		[setValue],
	);

	const {
		preCheckData,
		isPerformingPreCheck,
		hasConflicts,
		invoiceTotalIsInvalid,
	} = useInvoiceDetailsByDeliveryPeriod({
		invoiceId: pendingInvoiceData.id,
		deliveryStartDate,
		deliveryEndDate,
		originalStartDate: pendingInvoiceData.deliveryStartDate,
		originalEndDate: pendingInvoiceData.deliveryEndDate,
		onPreCheckComplete: handlePreCheckComplete,
	});

	// ── Merged display values (reflect pre-check when dates changed) ──

	const allStaffingsClosed = preCheckData
		? preCheckData.allStaffingsClosed
		: pendingInvoiceData.allStaffingsClosed;

	const hasFreelancers = preCheckData
		? preCheckData.hasFreelancers
		: pendingInvoiceData.hasFreelancers;

	const staffingsClosingStatuses = preCheckData
		? preCheckData.staffingsClosingStatuses
		: pendingInvoiceData.staffingsClosingStatuses;

	// ── Submit logic ──────────────────────────────────────────────────

	const submissionBlockedByInvoiceDetails = hasConflicts || !allStaffingsClosed || hasMissingTimeTrackings;
	const submissionBlockedByLoadingState = isPerformingPreCheck || isBusy;
	const submissionBlockedByFormState = !isValid || invoiceTotalIsInvalid;
	const submitIsDisabled = submissionBlockedByInvoiceDetails || submissionBlockedByLoadingState || (isSubmitted && submissionBlockedByFormState);

	const onSubmit = useCallback(
		async (data: FormData) => {
			if (!allStaffingsClosed || invoiceTotalIsInvalid || hasConflicts) {
				return;
			}
			try {
				setIsBusy(true);

				const formattedDeliveryStartDate = formatDateToYYYYMMDD(data.deliveryStartDate);
				const formattedDeliveryEndDate = formatDateToYYYYMMDD(data.deliveryEndDate);

				const sharedPayload = {
					comment: data.comment,
					deliveryStartDate: formattedDeliveryStartDate,
					deliveryEndDate: formattedDeliveryEndDate,
					paymentTargetDays: data.paymentTargetDays,
					subtotalCents: data.subtotalCents,
					totalAmountCents: data.totalAmountCents,
					travelCostCentsBeforeVat: data.travelCostCentsBeforeVat,
					vatPercentage: data.vatPercentage,
				};

				if (selectedInvoiceTypeId === invoiceTypeId.SelfBilledInvoice) {
					await updatePendingSelfBilledInvoiceStatusToDraft(pendingInvoiceData.id, {
						...sharedPayload,
						invoiceNumber: data.invoiceNumber,
						senderInformation: data.senderInformation,
						receivedAt: formatDateToYYYYMMDD(data.invoiceReceivedAt),
					});
				} else {
					await updatePendingRegularOrAdvanceInvoiceStatusToDraft(pendingInvoiceData.id, {
						...sharedPayload,
						invoiceTypeId: selectedInvoiceTypeId,
					});
				}
				onSuccess();
			} catch (error) {
				console.log(error);
				setServerErrorMessage(t("form:unexpectedError"));
				setIsBusy(false);
			}
		},
		[allStaffingsClosed, invoiceTotalIsInvalid, hasConflicts, selectedInvoiceTypeId, pendingInvoiceData.id, onSuccess],
	);

	// ── Render ────────────────────────────────────────────────────────

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex h-full flex-col"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Abrechnungsvorgang starten</SidebarHeaderHeadline>
				<div className="text-sm leading-tight text-gray-500">
					Beim Absenden dieses Formulars wird ein neuer Abrechnungsvorgang gestartet. Alle Daten können bei
					Bedarf später noch angepasst werden.
				</div>
			</SidebarHeader>

			<div className="relative grow overflow-auto px-7 py-2">
				{(isBusy || isPerformingPreCheck) && <SidebarBusyOverlay />}
				{!!serverErrorMessage && (
					<SidebarErrorOverlay title="Update fehlgeschlagen">
						{serverErrorMessage}
					</SidebarErrorOverlay>
				)}

				{pendingInvoiceData.hasOverbookedOrder && (
					<>
						<Hint size="sm"
							  theme="error">
							Das Kostenvolumen der Bestellung ist überbucht.
						</Hint>
						<div className="h-2" />
					</>
				)}

				{hasMissingTimeTrackings && (
					<>
						<Hint size="sm"
							  theme="warning">
							Der Abrechnungsvorgang kann nicht gestartet werden, da keine
							Erfassungen in dem Leistungszeitraum vorhanden sind. Bitte lade die Seite neu, die Rechnung
							wird dabei automatisch entfernt.
						</Hint>
						<div className="h-2" />
					</>
				)}

				<div className="grid grid-cols-2 gap-x-16">
					{/* ──────────────── LEFT COLUMN ──────────────── */}
					<div className="flex flex-col gap-8">
						{/* Project details */}
						<div>
							<Headline
								type="h4">
								Projektdetails
							</Headline>
							<dl className="divide-y divide-gray-100">
								<InvoiceDetailsElement label="Kunde">
									<div className="flex items-center gap-x-2">
										{pendingInvoiceData.companyAvatarImage && (
											<CompanyLogo
												image={pendingInvoiceData.companyAvatarImage}
												sizeClassName="size-6"
											/>
										)}
										<div className="truncate">
											{pendingInvoiceData.companyDisplayName}
										</div>
									</div>
								</InvoiceDetailsElement>
								<InvoiceDetailsElement label="Projekt">
									<NavLink
										to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
											projectId: pendingInvoiceData.projectId,
											tabId: ProjectDetailsTabName.Invoices,
										})}
										className="hover:text-secondary-500"
									>
										{`${pendingInvoiceData.projectCounter ? pendingInvoiceData.projectCounter + " - " : ""}${pendingInvoiceData.projectTitle}`}
									</NavLink>
								</InvoiceDetailsElement>
								<InvoiceDetailsElement label={phaseSectionTitle}>
									{phaseTitles}
								</InvoiceDetailsElement>
								{!isAdvanceInvoice && (
									<InvoiceDetailsElement label="Bestellung">
										{orderTitle}
									</InvoiceDetailsElement>
								)}
								<InvoiceDetailsElement label="Manager">
									{orderManagerFullName || EMPTY_VALUE_PLACEHOLDER}
								</InvoiceDetailsElement>
								{!isAdvanceInvoice && (
									<InvoiceDetailsElement label="Freelancer">
										{hasFreelancers ? (
											<CheckCircleIcon className="size-5 fill-success-600" />
										) : (
											<XCircleIcon className="size-5 fill-red-500" />
										)}
									</InvoiceDetailsElement>
								)}
							</dl>
						</div>

						{/* Order comment (read-only) */}
						<div>
							<Headline
								type="h4">
								Kommentar Bestellung
							</Headline>
							{nl2br(pendingInvoiceData.orderComment) || EMPTY_VALUE_PLACEHOLDER}
						</div>

						{/* Editable comment */}
						<div>
							<Headline
								type="h4">
								Kommentar Rechnungssteller:in
							</Headline>
							<Textarea
								control={control}
								name="comment"
								placeholder="Hier können Besonderheiten zum Abrechnungsprozess vermerkt werden."
							/>
						</div>

						{/* Monthly closing statuses (hidden for advance invoices) */}
						{!isAdvanceInvoice && (
							<div>
								<Headline
									type="h4">
									Status Monatsabschluss
								</Headline>
								<div className="mt-1 flex w-full flex-row flex-wrap gap-1">
									<UsersMonthlyClosingStatus
										allUsersData={allUsersData}
										staffingsClosingStatuses={staffingsClosingStatuses}
									/>
								</div>
								{!allStaffingsClosed && (
									<div className="mt-2">
										<Hint size="sm"
											  theme="error">
											Der Abrechnungsvorgang kann erst gestartet werden, wenn
											alle Teammitglieder ihre Monatsabschlüsse für den
											gewählten Leistungszeitraum getätigt haben.
										</Hint>
									</div>
								)}
							</div>
						)}
					</div>

					{/* ──────────────── RIGHT COLUMN ──────────────── */}
					<div className="flex flex-col gap-8 rounded-lg border px-8 py-4">
						{/* Invoice type selector / display */}
						<div>
							<Headline
								type="h4">
								Abrechnungsart
							</Headline>
							{!isAdvanceInvoice && (
								<SelectPlain
									optionsData={invoiceTypeSelectOptions}
									value={selectedInvoiceTypeId}
									onChange={setSelectedInvoiceTypeId}
								/>)
							}
							{isAdvanceInvoice && (<div className="font-bold">
									{pendingInvoiceData.quota ? <NavLink to={generatePath(QUOTA_DETAILS_PATH, {
										clientId: pendingInvoiceData.quota.clientId,
										quotaId: pendingInvoiceData.quota.id,
									})}
																		 className=" flex items-center gap-x-1 text-primary-400 hover:text-secondary-500"
									>
										Vorauszahlungsrechnung /
										Abrechnungskontingent <ArrowTopRightOnSquareIcon className="size-4" />
									</NavLink> : <span>Vorauszahlungsrechnung / Abrechnungskontingent</span>}

								</div>
							)}
							{isSelfBilled && <>
								<div className="h-2" />
								<Hint size="sm">
									Im Falle einer Gutschrift wird kein Rechnungsdokument von unserer Seite erstellt.
									Der Vorgang dient lediglich der internen Dokumentation und Prüfung des
									Zahlungseingangs.
								</Hint></>}
							{isAdvanceInvoice && <>
								<div className="h-2" />
								<Hint size="sm">
									Vorauszahlungsrechnungen werden auf Basis eines mit dem Kunden vereinbarten Budgets
									vorab in Rechnung gestellt. Die Details der Rechnung können nur direkt im
									Abrechnungskontingent geändert werden.
								</Hint></>}
						</div>

						{/* Self-billed specific fields */}
						{isSelfBilled && (
							<div>
								<Headline
									type="h4">
									Gutschrift- / Referenznummer
								</Headline>
								<Input
									control={control}
									name="invoiceNumber"
									placeholder="Rechnungsnummer Kunde"
								/>
								<div className="mb-4 mt-2 text-xs text-gray-400">
									Hier bitte die Rechnungsnummer eintragen, die auf der
									Gutschrift des Kunden zu finden ist.
								</div>
								<div className="grid grid-cols-2 gap-x-4">
									<Input
										control={control}
										name="senderInformation"
										label="Absender"
										placeholder="Name des Absenders, ggf. E-Mail-Adresse"
									/>
									<DatePickerInput
										name="invoiceReceivedAt"
										clearable={false}
										control={control}
										label="Eingegangen am"
									/>
								</div>
							</div>
						)}

						{/* Delivery period */}
						<div>
							<Headline
								type="h4">
								Leistungszeitraum
							</Headline>
							{isAdvanceInvoice ? (
								<div>
									{formatSmartDateRange(
										pendingInvoiceData.deliveryStartDate,
										pendingInvoiceData.deliveryEndDate,
										{ month: "MMMM", year: " yyyy" },
									)}
								</div>
							) : (
								<div className="grid w-full grid-cols-2 gap-x-4">
									<DatePickerInput
										name="deliveryStartDate"
										clearable={false}
										control={control}
										label="Start"
									/>
									<DatePickerInput
										name="deliveryEndDate"
										clearable={false}
										control={control}
										maxDate={endOfCurrentMonth}
										label="Ende"
									/>

									{/* Conflict warnings */}
									{!isPerformingPreCheck && hasConflicts && (
										<div className="col-span-2">
											<FormInputError>
												In dem ausgewählten Leistungszeitraum existieren bereits
												Rechnungen für diese Bestellung:
											</FormInputError>
											{preCheckData?.conflicts.map((conflict, index) => (
												<div
													key={index}
													className="my-2 flex w-full items-center justify-between rounded border border-red-500 p-2 text-sm text-red-500"
												>
													<div>{conflict.invoiceNumber}</div>
													<div>
														{formatSmartDateRange(
															new Date(conflict.deliveryStartDate),
															new Date(conflict.deliveryEndDate),
															{ month: "MMMM", year: " yyyy" },
														)}
													</div>
												</div>
											))}
										</div>
									)}

									{/* No billable entries warning */}
									{isValid && !isPerformingPreCheck && invoiceTotalIsInvalid && (
										<div className="col-span-2">
											<FormInputError>
												In diesem Zeitraum liegen keine abrechenbaren
												Erfassungen vor.
											</FormInputError>
										</div>
									)}
								</div>
							)}
						</div>

						<div>
							<Headline
								type="h4">
								Zahlungsziel
							</Headline>
							<div className="flex w-24 items-center gap-x-1">
								{!isAdvanceInvoice && <Input
									textAlign="center"
									control={control}
									name="paymentTargetDays"
									type="number"
									placeholder="z.B. 30"
								/>}
								<span>{isAdvanceInvoice && pendingInvoiceData.paymentTargetDays + " "}Tage</span>
							</div>
						</div>

						{/* Invoice totals */}
						<InvoiceTotalsFormSection<FormData>
							control={control}
							errorMessage={
								errors?.totalAmountCents?.message ||
								errors?.subtotalCents?.message
							}
							watch={watch}
							setValue={setValue}
							title="Rechnungsbetrag"
							readOnlySubtotal={isAdvanceInvoice}
							includeTravelCosts={!isAdvanceInvoice}
						/>
					</div>
				</div>
			</div>

			<SidebarFooter>
				<div className="mr-auto">
					<Button theme="none"
							onClick={handleDownload}>
						<WordIcon className="size-5" />
						Voransicht
						{isGenerating && (
							<LoadingSpinner scale={0.7}
											theme="neutral" />
						)}
					</Button>
				</div>
				<FormHasErrorsHint
					show={(isSubmitted && !isValid) || !allStaffingsClosed}
					className="mr-2"
				/>
				{userCanManageInvoices && !hasMissingTimeTrackings && (
					<SubmitButton busy={isBusy}
								  disabled={submitIsDisabled}>
						Abrechnungsvorgang starten
					</SubmitButton>
				)}
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default StartBillingProcessForProjectInvoicesForm;